/**
 * Tiny, asset-free notification chime for incoming chat messages. Synthesised
 * with the Web Audio API (a gentle two-note "ding") so there's no audio file to
 * ship or preload. Browsers only allow audio after a user gesture; by the time a
 * kid is in a chat they've already tapped around, so the context resumes fine.
 */
let ctx: AudioContext | null = null;

const MUTE_KEY = 'kidzim_chat_muted';

export function isChatMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setChatMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore storage failures (private mode etc.) */
  }
}

export function playDing(): void {
  if (isChatMuted()) return;
  try {
    const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    ctx ??= new AudioCtx();
    if (ctx.state === 'suspended') void ctx.resume();

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(784, now); // G5
    osc.frequency.setValueAtTime(1175, now + 0.09); // D6 — a friendly little lift

    // Quick attack, soft decay so it's a chime, not a buzz.
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.13, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.3);

    osc.start(now);
    osc.stop(now + 0.32);
  } catch {
    /* never let a sound failure break the chat */
  }
}
